import styled from '@emotion/styled';
import { claudeTokens } from '@/styles/claudeTokens';
import { designSystem } from '@/styles/theme';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import { withSentenceLines } from '@/utils/sentenceLines';

/**
 * 대화방 화면 색·크기 — 새 hex 없이 기존 토큰에서만(UI 가이드 1장).
 * 시안(hire-chat)의 구성: 위 바 · 회색 바탕 타임라인(카드·말풍선) · 아래 입력줄.
 */
export const threadColor = {
    text: FORM_COLOR.text,
    sub: FORM_COLOR.sub,
    muted: FORM_COLOR.muted,
    placeholder: FORM_COLOR.placeholder,
    border: FORM_COLOR.border,
    line: FORM_COLOR.line,
    lineSoft: FORM_COLOR.lineSoft,
    surface: claudeTokens.color.surface,
    bg: claudeTokens.color.bg,
    primary: designSystem.colors.primary,
    primaryDeep: FORM_COLOR.primaryDeep,
    primarySoft: FORM_COLOR.primarySoft,
    primaryDisabled: FORM_COLOR.primaryDisabled,
    danger: FORM_COLOR.danger,
    accent: claudeTokens.color.accent,
} as const;

/** 폰 폭 — 앱 하단 탭·공용 시트 기준과 같다 */
export const THREAD_MOBILE_MAX_WIDTH_PX: number = 768;

/** 카드 안 동작 버튼 — 높이 40, 좌우 같은 여백 */
export const CardButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger' }>`
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 72px;
    height: 40px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid ${({ variant }) => (variant === 'primary' ? threadColor.primary : threadColor.border)};
    background: ${({ variant }) => (variant === 'primary' ? threadColor.primary : threadColor.surface)};
    color: ${({ variant }) => (variant === 'primary' ? '#fff' : variant === 'danger' ? threadColor.danger : threadColor.text)};
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
    &:focus-visible {
        outline: 2px solid ${threadColor.primary};
        outline-offset: 2px;
    }
`;

/** 시트·창 입력 묶음 */
export const SheetBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding-bottom: 4px;
`;

export const SheetField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

export const SheetLabel = styled.label`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: ${threadColor.sub};

    b {
        color: ${threadColor.danger};
        margin-left: 2px;
    }

    small {
        font-size: 12px;
        font-weight: 500;
        color: ${threadColor.muted};
        font-variant-numeric: tabular-nums;
    }
`;

/** 입력 칸 — UI 가이드 3장(52px·반경 12·테두리, 포커스 청록, 오류 빨강) */
export const SheetInput = styled.input`
    width: 100%;
    height: 52px;
    padding: 0 14px;
    box-sizing: border-box;
    border: 1px solid ${threadColor.border};
    border-radius: 12px;
    background: ${threadColor.surface};
    font-family: inherit;
    font-size: 16px;
    color: ${threadColor.text};
    min-width: 0;

    &::placeholder {
        color: ${threadColor.placeholder};
    }
    &:focus {
        outline: none;
        border-color: ${threadColor.primary};
    }
    &[aria-invalid='true'] {
        border-color: ${threadColor.danger};
    }
`;

export const SheetTextArea = styled.textarea`
    width: 100%;
    min-height: 96px;
    padding: 12px 14px;
    box-sizing: border-box;
    border: 1px solid ${threadColor.border};
    border-radius: 12px;
    background: ${threadColor.surface};
    font-family: inherit;
    font-size: 16px;
    line-height: 1.5;
    color: ${threadColor.text};
    resize: none;

    &::placeholder {
        color: ${threadColor.placeholder};
    }
    &:focus {
        outline: none;
        border-color: ${threadColor.primary};
    }
    &[aria-invalid='true'] {
        border-color: ${threadColor.danger};
    }
`;

/** 칸 바로 아래 오류 문구 */
export const FieldError = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${threadColor.danger};
`;

/** 시트 아래 안내(작은 회색) — 문장마다 줄바꿈(UI 가이드 5장) */
const SheetHelpText = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${threadColor.muted};
    white-space: pre-line;
    word-break: keep-all;
`;
export const SheetHelp = withSentenceLines(SheetHelpText);

/** 시트·창 하단 버튼 줄 — [닫기][주 동작] */
export const SheetActions = styled.div`
    display: flex;
    gap: 10px;
    width: 100%;
`;

/** [danger] 는 되돌릴 수 없는 주 동작(입사 포기 등) — 빨강 바탕(UI 가이드 1장 오류색 한 가지) */
export const SheetButton = styled.button<{ primary?: boolean; danger?: boolean }>`
    appearance: none;
    flex: ${({ primary }) => (primary ? '1 1 auto' : '0 0 100px')};
    min-width: 0;
    height: 52px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    border-radius: 14px;
    border: ${({ primary }) => (primary ? 'none' : `1px solid ${threadColor.border}`)};
    background: ${({ primary, danger }) => (primary ? (danger ? threadColor.danger : threadColor.primary) : threadColor.surface)};
    color: ${({ primary }) => (primary ? '#fff' : threadColor.sub)};
    font-family: inherit;
    font-size: 16px;
    font-weight: 800;
    white-space: nowrap;
    cursor: pointer;

    &:disabled {
        background: ${({ primary }) => (primary ? threadColor.primaryDisabled : threadColor.surface)};
        cursor: default;
    }
`;
